import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserPlus, Lifebuoy, UsersThree, CurrencyEur, ArrowRight } from "@phosphor-icons/react";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";

const fmt = (iso) => {
  try { return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso || ""; }
};

export default function AdminDashboard() {
  const { t } = useApp();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="font-body text-gray-500">…</p>;
  if (!stats) return <p className="font-body text-gray-500">Statistiken konnten nicht geladen werden.</p>;

  const cur = stats.currency || "EUR";
  const money = new Intl.NumberFormat("de-DE", { style: "currency", currency: cur }).format(stats.revenue || 0);

  const cards = [
    { id: "registrations", label: "Registrierungen", value: stats.total_users, Icon: UserPlus, color: "#A855F7", to: "/admin/registrations" },
    { id: "tickets", label: "Offene Tickets", value: stats.open_tickets, Icon: Lifebuoy, color: "#EF4444", to: "/admin/tickets" },
    { id: "active", label: "Aktive Nutzer", value: stats.active_users, Icon: UsersThree, color: "#22C55E", to: "/admin/buddies" },
    { id: "revenue", label: "Umsatz", value: money, Icon: CurrencyEur, color: "#F59E0B", to: "/admin/shop" },
  ];

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div className="grid grid-cols-2 gap-3">
        {cards.map((c) => (
          <button key={c.id} data-testid={`admin-stat-${c.id}`} onClick={() => navigate(c.to)}
            className="sb-card p-4 flex flex-col gap-1 text-left active:scale-[0.98] transition-transform">
            <div className="w-10 h-10 rounded-2xl flex items-center justify-center mb-1" style={{ background: `color-mix(in srgb, ${c.color} 18%, transparent)` }}>
              <c.Icon size={22} weight="bold" style={{ color: c.color }} />
            </div>
            <span className="text-2xl lg:text-3xl font-heading font-semibold" style={{ color: "var(--app-text, inherit)" }}>{c.value}</span>
            <span className="text-xs font-body text-gray-500 leading-tight">{c.label}</span>
          </button>
        ))}
      </div>

      <div className="sb-card p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading font-semibold flex items-center gap-2"><UserPlus size={20} weight="duotone" style={{ color: "var(--app-strong)" }} />Neueste Registrierungen</h2>
          <button data-testid="dashboard-all-registrations" onClick={() => navigate("/admin/registrations")}
            className="font-heading text-xs font-medium flex items-center gap-1" style={{ color: "var(--app-strong)" }}>
            Alle anzeigen <ArrowRight size={14} weight="bold" />
          </button>
        </div>
        {(!stats.recent_users || stats.recent_users.length === 0) && <p className="font-body text-sm text-gray-500">Noch keine Registrierungen.</p>}
        <div className="space-y-2" data-testid="dashboard-recent-users">
          {(stats.recent_users || []).slice(0, 8).map((u, i) => (
            <div key={i} data-testid={`dashboard-user-${i}`} className="flex items-center justify-between gap-2 border-2 border-[var(--app-border)] rounded-2xl px-3 py-2 bg-[var(--app-surface)]">
              <div className="min-w-0">
                <p className="font-heading text-sm truncate">{[u.first_name, u.last_name].filter(Boolean).join(" ") || "—"}</p>
                <p className="font-body text-xs text-gray-500 truncate">{u.email}</p>
              </div>
              <span className="font-body text-[11px] text-gray-400 shrink-0">{fmt(u.created_at)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
